/**
 * #752 — a paste whose clipboard came from ANOTHER tenant is a cross-tenant
 * copy: real bytes materialise across an org boundary. Every entry point (the
 * drive's Paste button and Ctrl/Cmd+V, the Projects/MiniApps paste) asks first,
 * naming both sides. Same-tenant paste is unaffected.
 *
 * The clipboard carries the source's display label from COPY time
 * (`sourceLabel`), because by paste time the user has switched away and the
 * destination no longer has the source tenant loaded.
 */
import type { ReactNode } from 'react'
import { request } from '@papan/api-client'

type T = (key: string, options?: Record<string, unknown>) => string
type Confirm = (options: { title: string; message: string | ReactNode; type?: 'info' | 'warning' | 'danger'; confirmText?: string }) => Promise<boolean>

/** How a drive is named to a person: an org by its name, a personal drive as
 *  "<name>'s Drive" (a bare personal name reads like a person, not a place). */
export function driveLabel(t: T, isOrg: boolean | undefined, tenantName: string | undefined): string | undefined {
  const name = tenantName?.trim()
  if (!name) return undefined
  return isOrg ? name : t('driveView.title', { tenant: name })
}

/**
 * The destination's label, resolved NOW if the prop hasn't arrived. `tenantName`
 * comes from App's `my-tenants` fetch, which a fast paste can beat — and a
 * confirm message is built once and frozen into the open dialog, so it read
 * "into \"another drive\"" under a header that already named the org (caught by
 * e2e/cross-tenant-paste-confirm.spec.ts). Fails soft to the generic label.
 * A personal drive is named from `tenants.name` (the signup-time name), which a
 * later profile rename doesn't update — cosmetic, and only on this fast path.
 */
export async function resolveDriveLabel(
  t: T, tenantId: string | null | undefined, isOrg: boolean | undefined, tenantName: string | undefined,
): Promise<string | undefined> {
  const known = driveLabel(t, isOrg, tenantName)
  if (known || !tenantId) return known
  try {
    // Tight bound: this sits between Ctrl+V and the dialog with the paste guard
    // held, so a slow fetch must degrade to the generic label, not a dead key.
    const { tenants } = await request<{ tenants: Array<{ id: string; name: string; type: string }> }>(
      '/api/orgs/my-tenants', { signal: AbortSignal.timeout(3000) },
    )
    const hit = tenants.find((x) => x.id === tenantId)
    return hit ? driveLabel(t, hit.type === 'organization', hit.name) : undefined
  } catch {
    return undefined
  }
}

export function confirmCrossTenantPaste(
  confirm: Confirm,
  t: T,
  { count, from, to }: { count: number; from?: string; to?: string },
): Promise<boolean> {
  const fallback = t('driveView.crossTenantPaste.anotherDrive')
  return confirm({
    title: t('driveView.crossTenantPaste.title'),
    message: t('driveView.crossTenantPaste.message', { count, from: from || fallback, to: to || fallback }),
    type: 'info',
    confirmText: t('driveView.crossTenantPaste.confirm'),
  })
}
